import { useState, useRef, useCallback, useEffect } from "react";

interface UseCanvasPanZoomOptions {
    zoomMin?: number;
    zoomMax?: number;
    zoomStep?: number;
}

export const useCanvasPanZoom = (options: UseCanvasPanZoomOptions = {}) => {
    const { zoomMin = 0.25, zoomMax = 2, zoomStep = 0.08 } = options;

    const canvasRef = useRef<HTMLDivElement>(null);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const dragState = useRef({
        dragging: false, startX: 0, startY: 0, startPanX: 0, startPanY: 0,
    });

    const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        if ((e.target as HTMLElement).closest(".flow-diagram__step-card, .flow-diagram__agent-card, button")) {
            return;
        }
        dragState.current = { dragging: true, startX: e.clientX, startY: e.clientY, startPanX: pan.x, startPanY: pan.y };
        canvasRef.current?.setPointerCapture(e.pointerId);
    }, [pan]);

    const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        if (!dragState.current.dragging) return;
        const dx = e.clientX - dragState.current.startX;
        const dy = e.clientY - dragState.current.startY;
        setPan({ x: dragState.current.startPanX + dx, y: dragState.current.startPanY + dy });
    }, []);

    const handlePointerUp = useCallback(() => {
        dragState.current.dragging = false;
    }, []);

    useEffect(() => {
        const el = canvasRef.current;
        if (!el) return;
        const onWheel = (e: WheelEvent) => {
            e.preventDefault();
            setZoom((prev) => {
                const next = prev - Math.sign(e.deltaY) * zoomStep;
                return Math.min(zoomMax, Math.max(zoomMin, Math.round(next * 100) / 100));
            });
        };
        el.addEventListener("wheel", onWheel, { passive: false });
        return () => el.removeEventListener("wheel", onWheel);
    }, [zoomMin, zoomMax, zoomStep]);

    const handleZoomIn = useCallback(() => {
        setZoom((z) => Math.min(zoomMax, Math.round((z + zoomStep) * 100) / 100));
    }, [zoomMax, zoomStep]);

    const handleZoomOut = useCallback(() => {
        setZoom((z) => Math.max(zoomMin, Math.round((z - zoomStep) * 100) / 100));
    }, [zoomMin, zoomStep]);

    const handleZoomReset = useCallback(() => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
    }, []);

    return {
        canvasRef,
        pan,
        zoom,
        handlePointerDown,
        handlePointerMove,
        handlePointerUp,
        handleZoomIn,
        handleZoomOut,
        handleZoomReset,
    };
};
